import React from 'react'
import styled, { css } from 'styled-components'

import { OutlineFilter } from 'components/lib/DesignSystem/Icons'
import Badge, { SHAPES, SIZES as BADGE_SIZES } from 'components/lib/DesignSystem/V3/Badge'
import Button, { SIZES as BUTTON_SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'

const animationEasing = 'cubic-bezier(0.22, 1, 0.36, 1)'

const Wrapper = styled.div`
  overflow: hidden;
  max-height: 0;
  margin-bottom: 0;
  opacity: 0;
  transform: translateY(-6px);
  pointer-events: none;
  transition:
    max-height 280ms ${animationEasing},
    margin-bottom 280ms ${animationEasing},
    opacity 220ms ease,
    transform 280ms ${animationEasing};

  ${({ $visible }) =>
    $visible &&
    css`
      max-height: 200px;
      margin-bottom: 20px;
      opacity: 1;
      transform: translateY(0);
      pointer-events: auto;
    `}
`

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: var(--color-surface);
  border: 1px solid var(--card-border-color);
  border-radius: var(--t-borders-radius-rounded-2xl);
  box-shadow: var(--psima-smart-filter-shadow);
`

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`

const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const FilterLabel = styled.div`
  color: var(--sub-text-color);
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  font-weight: var(--t-typography-font-weight-regular);
  white-space: nowrap;
`

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
`

const ResultsLabel = styled.div`
  color: var(--hint-color);
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  white-space: nowrap;
`

const FilterBadge = styled(Badge)`
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  font-weight: var(--t-typography-font-weight-medium);
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.05));
`

const SmartFilter = ({ isVisible, label, resultsLabel, onClear }) => (
  <Wrapper $visible={isVisible} aria-hidden={!isVisible} data-testid="Psima_Dashboard_SmartFilter">
    <Card data-testid="Psima_Dashboard_SmartFilter_Card">
      <LeftGroup>
        <LabelGroup>
          <OutlineFilter width="16px" height="16px" fill="var(--sub-text-color)" />
          <FilterLabel>Filtered by:</FilterLabel>
        </LabelGroup>
        <FilterBadge
          dataTestId="Psima_Dashboard_SmartFilter_Badge"
          size={BADGE_SIZES.LARGE}
          shape={SHAPES.SQUARE}
          color="var(--psima-smart-filter-badge-bg)"
          textColor="var(--color-text)"
        >
          {label}
        </FilterBadge>
      </LeftGroup>

      <RightGroup>
        <ResultsLabel data-testid="Psima_Dashboard_SmartFilter_Results">
          {resultsLabel}
        </ResultsLabel>
        <Button
          dataTestId="Psima_Dashboard_SmartFilter_ClearFilters_Button"
          variant={VARIANTS.SECONDARY}
          size={BUTTON_SIZES.XS}
          onClick={onClear}
        >
          Clear filters
        </Button>
      </RightGroup>
    </Card>
  </Wrapper>
)

export default SmartFilter
